import { Component } from '@theme/component';
import {
  SPEED_AXLE_RULES,
  getValidOptions,
  formatBoltPattern,
  ARM_ANGLE_MATRIX,
  BRACKET_STYLE_LABELS,
  buildPartNumber,
  brakeTypeLabel,
  RIDE_HEIGHT,
  rideHeightFor,
  rideHeightAngleLabel
} from '@theme/speed-axle-rules';

const UNAVAILABLE_CAPACITIES = SPEED_AXLE_RULES.unavailableCapacities || [];

/* Options Hydrastar is not offering right now, from the rules. A paused
   option is hidden in the builder and skipped by the gating, the summary
   and the order properties, so withdrawing one deletes no code. */
const PAUSED_OPTIONS = SPEED_AXLE_RULES.pausedOptions || [];
const OFFER_SIDE_MOUNT = !PAUSED_OPTIONS.includes('sideMountInstalled');

const ALL_PATTERNS = ['4x4', '5x4.5', '5x4.75', '5x5', '5x5.5', '6x5.5', '8x6.5'];
const ALL_STUDS = ['1/2"', '9/16"', '5/8"'];
const ALL_BRACKETS = ['single', 'double', 'heavyDuty'];

const SUMMARY_ROWS = [
  ['capacity', 'Capacity'],
  ['hubs', 'Hubs'],
  ['wheelConfig', 'Wheel config'],
  ['beamHardware', 'Hardware'],
  ['boltPattern', 'Bolt pattern'],
  ['studSize', 'Stud size'],
  ['hubFace', 'Hub face'],
  ['outsideBracket', 'Outside bracket'],
  ['armAngle', 'Arm angle'],
  ['tubeProfile', 'Tube profile'],
  ['bracketStyle', 'Bracket style'],
  ['bracketOrientation', 'Orientation'],
  ['sideMountInstalled', 'Side mounts installed'],
  ['finish', 'Finish'],
  ['fluidType', 'Fluid type'],
  ['quantity', 'Quantity'],
];

const ORIENTATION_LABELS = {
  ST: 'Standard Top Mount, Outboard (ST)',
  RM: 'Reverse Side Mount, Inboard (RM)',
};

/**
 * Guided axle builder. Consumes SPEED_AXLE_RULES as the single
 * source of truth: every option set, forced selection, and
 * measurement window comes from the rules module. Invalid
 * combinations are disabled before they can be selected.
 */
class SpeedAxleConfiguratorComponent extends Component {
  requiredRefs = ['summary', 'submitButton', 'hubFaceInput', 'bracketInput'];

  state = {
    capacity: null,
    hubType: null,
    brakeType: null,
    drumType: null,
    wheelConfig: null,
    beamHardware: null,
    boltPattern: null,
    studSize: null,
    hubFace: '',
    outsideBracket: '',
    armAngle: null,
    armPosition: null,
    tubeProfile: null,
    bracketStyle: null,
    bracketOrientation: null,
    sideMountInstalled: null,
    finish: null,
    fluidType: null,
    quantity: 1,
    po: '',
    urgency: 'standard',
    customerFirstName: '',
    customerLastName: '',
    customerCompany: '',
    customerEmail: '',
    customerPhone: '',
    customerAddress1: '',
    customerAddress2: '',
    customerCity: '',
    customerState: '',
    customerZip: '',
    termsAccepted: false,
  };

  connectedCallback() {
    super.connectedCallback();
    this.#renderOptionGroups();
    this.#applyPauses();
    this.#preselectFromUrl();
    this.#apply();
  }

  /* Paused options ship hidden in the markup and are revealed here when the
     rules offer them, so a paused option never flashes before the script
     runs. */
  #applyPauses() {
    for (const ref of ['sideMountBlock', 'sideMountHelp']) {
      if (this.refs[ref]) this.refs[ref].hidden = !OFFER_SIDE_MOUNT;
    }
  }

  /* Capacity landing pages deep link the builder: /?capacity=7K#configurator */
  #preselectFromUrl() {
    const requested = new URLSearchParams(window.location.search).get('capacity');
    /* A capacity page can deep link a capacity we are not building yet, so the
       preselect has to respect the same list the options do. */
    if (
      requested &&
      SPEED_AXLE_RULES.capacities[requested] &&
      !UNAVAILABLE_CAPACITIES.includes(requested)
    ) {
      this.state.capacity = requested;
      const radio = this.querySelector('input[name="capacity"][value="' + CSS.escape(requested) + '"]');
      if (radio) radio.checked = true;
    }
  }

  /* ------------------------------------------------------------
     Option group rendering. Full catalogs render once; apply()
     toggles disabled and checked so focus is never destroyed.
     ------------------------------------------------------------ */
  #renderOptionGroups() {
    const caps = SPEED_AXLE_RULES.capacities;
    this.#renderGroup(
      this.refs.capacityOptions,
      'capacity',
      Object.keys(caps).map((key) => ({
        value: key,
        label: caps[key].label,
        sub: UNAVAILABLE_CAPACITIES.includes(key) ? 'Coming soon' : null,
        disabled: UNAVAILABLE_CAPACITIES.includes(key),
      }))
    );

    /* Brake type, then the drum type under it. Both come from the rules, so
       adding a brake option is a config edit. */
    this.#renderGroup(
      this.refs.brakeTypeOptions,
      'brakeType',
      SPEED_AXLE_RULES.brakeTypes.map((b) => ({ value: b.value, label: b.label, sub: b.sub || null }))
    );
    this.#renderGroup(
      this.refs.drumTypeOptions,
      'drumType',
      SPEED_AXLE_RULES.drumTypes.map((d) => ({ value: d.value, label: d.label, sub: d.sub || null }))
    );

    this.#renderGroup(
      this.refs.boltPatternOptions,
      'boltPattern',
      ALL_PATTERNS.map((key) => ({ value: key, label: formatBoltPattern(key) }))
    );

    this.#renderGroup(
      this.refs.studSizeOptions,
      'studSize',
      ALL_STUDS.map((key) => ({ value: key, label: key + ' studs' }))
    );

    this.#renderGroup(
      this.refs.bracketStyleOptions,
      'bracketStyle',
      ALL_BRACKETS.map((key) => ({
        value: key,
        label: BRACKET_STYLE_LABELS[key] || key,
        sub: key === 'heavyDuty' ? '8K and above' : null,
      }))
    );

    this.#renderGroup(
      this.refs.tubeProfileOptions,
      'tubeProfile',
      SPEED_AXLE_RULES.tubeProfiles.map((value) => ({ value, label: value }))
    );

    /* The galvanized lead time warning appears once Galvanized is chosen
       (the callout under the cards and the summary rail), not on the card
       itself. Anthony's call, Sept 2026. */
    this.#renderGroup(
      this.refs.finishOptions,
      'finish',
      SPEED_AXLE_RULES.finishes.map((value) => ({ value, label: value }))
    );

    this.#renderGroup(
      this.refs.fluidTypeOptions,
      'fluidType',
      SPEED_AXLE_RULES.fluidTypes.map((value) => ({ value, label: value }))
    );
  }

  #renderGroup(container, name, options) {
    if (!container) return;
    container.textContent = '';
    for (const option of options) {
      const label = document.createElement('label');
      label.className = 'sa-opt';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = name;
      input.value = option.value;
      input.setAttribute('on:change', '/handleInput');
      if (option.disabled) input.disabled = true;

      const body = document.createElement('span');
      body.className = 'sa-opt__body';

      const text = document.createElement('span');
      text.className = 'sa-opt__label';
      text.textContent = option.label;
      body.append(text);

      if (option.sub) {
        const sub = document.createElement('span');
        sub.className = 'sa-opt__sub';
        sub.textContent = option.sub;
        body.append(sub);
      }

      label.append(input, body);
      container.append(label);
    }
  }

  /* ------------------------------------------------------------
     Input handling
     ------------------------------------------------------------ */
  handleInput(event) {
    const input = event.target;
    const { name, value } = input;

    switch (name) {
      case 'capacity':
        this.state.capacity = value;
        break;
      case 'hubType':
        this.state.hubType = value;
        break;
      case 'brakeType':
        this.state.brakeType = value;
        break;
      case 'drumType':
        this.state.drumType = value;
        break;
      case 'wheelConfig':
        this.state.wheelConfig = value;
        break;
      case 'beamHardware':
        this.state.beamHardware = value;
        break;
      case 'boltPattern':
        this.state.boltPattern = value;
        break;
      case 'studSize':
        this.state.studSize = value;
        break;
      case 'hubFace':
        this.state.hubFace = input.value;
        break;
      case 'outsideBracket':
        this.state.outsideBracket = input.value;
        break;
      case 'tubeProfile':
        this.state.tubeProfile = value;
        break;
      case 'bracketStyle':
        this.state.bracketStyle = value;
        break;
      case 'bracketOrientation':
        this.state.bracketOrientation = value;
        break;
      case 'sideMountInstalled':
        this.state.sideMountInstalled = value;
        break;
      case 'finish':
        this.state.finish = value;
        break;
      case 'fluidType':
        this.state.fluidType = value;
        break;
      case 'quantity':
        this.state.quantity = Math.max(1, parseInt(input.value, 10) || 1);
        break;
      case 'po':
        this.state.po = input.value.trim();
        break;
      case 'urgency':
        this.state.urgency = input.value;
        break;
      case 'customerFirstName':
        this.state.customerFirstName = input.value;
        break;
      case 'customerLastName':
        this.state.customerLastName = input.value;
        break;
      case 'customerCompany':
        this.state.customerCompany = input.value;
        break;
      case 'customerEmail':
        this.state.customerEmail = input.value.trim();
        break;
      case 'customerPhone':
        this.state.customerPhone = input.value.trim();
        break;
      case 'customerAddress1':
        this.state.customerAddress1 = input.value;
        break;
      case 'customerAddress2':
        this.state.customerAddress2 = input.value;
        break;
      case 'customerCity':
        this.state.customerCity = input.value;
        break;
      case 'customerState':
        this.state.customerState = input.value;
        break;
      case 'customerZip':
        this.state.customerZip = input.value.trim();
        break;
      case 'termsAccepted':
        this.state.termsAccepted = input.checked;
        break;
    }

    this.#apply();
  }

  /* Dial: hidden radio change (keyboard) */
  handleDial(event) {
    this.#setAngle(event.target.value, event.target.dataset.dialDeg);
  }

  /* Video panel under the measurements. The iframe carries its address in
     data-src; the first click that opens the panel moves it to src, so the
     YouTube player only downloads for people who ask to watch. */
  handleVideoOpen(event) {
    const panel = event.currentTarget || this.refs.measureVideo;
    const frame = panel && panel.querySelector('iframe[data-src]');
    if (frame) {
      frame.src = frame.dataset.src;
      frame.removeAttribute('data-src');
    }
  }

  /* Dial: SVG hotspot click (pointer). SVG shapes ignore the
     disabled state of their fieldset, so guard the gate here. */
  handleDialClick(event) {
    const fieldset = event.target.closest('fieldset');
    if (fieldset && fieldset.disabled) return;
    const value = event.target.dataset.dialValue;
    const radio = this.querySelector('input[name="armAngleCombo"][value="' + CSS.escape(value) + '"]');
    if (radio) {
      radio.checked = true;
      this.#setAngle(value, radio.dataset.dialDeg);
    }
  }

  #setAngle(comboValue, deg) {
    const [angle, position] = comboValue.split('|');
    this.state.armAngle = angle;
    this.state.armPosition = position || null;

    if (this.refs.dialArm) {
      this.refs.dialArm.style.setProperty('--dial-angle', deg + 'deg');
    }

    for (const label of this.querySelectorAll('[data-dial-label]')) {
      label.classList.toggle('is-selected', label.dataset.dialLabel === comboValue);
    }
    for (const spot of this.querySelectorAll('[data-dial-value]')) {
      spot.classList.toggle('is-selected', spot.dataset.dialValue === comboValue);
    }
    if (this.refs.dialReadout) {
      this.refs.dialReadout.textContent = this.#angleText();
    }

    this.#apply();
  }

  #angleText() {
    if (this.state.armAngle == null) return 'Select an arm angle';
    if (this.state.armAngle === '0') return '0 degrees, straight';
    const dir = this.state.armPosition === 'upturn' ? 'upturn (-)' : 'downturn (+)';
    return this.state.armAngle + ' degrees, ' + dir;
  }

  /* ------------------------------------------------------------
     The single validation and UI pass
     ------------------------------------------------------------ */
  #apply() {
    const state = this.state;
    const valid = getValidOptions(state);

    if (valid) {
      /* cascade reset: clear anything no longer valid */
      if (state.boltPattern && !valid.boltPatterns.includes(state.boltPattern)) {
        state.boltPattern = null;
      }
      /* stud validity depends on the (possibly reset) pattern */
      const studs = this.#validStuds(valid);
      if (state.studSize && !studs.includes(state.studSize)) {
        state.studSize = null;
      }
      if (state.bracketStyle && !valid.bracketStyles.includes(state.bracketStyle)) {
        state.bracketStyle = null;
      }
      /* oil bath is 7K and up; drop it if the capacity no longer allows it */
      if (state.fluidType && !valid.fluidTypes.includes(state.fluidType)) {
        state.fluidType = null;
      }

      /* forced selections (10K and 12K) */
      if (valid.forced) {
        state.bracketStyle = valid.forced.bracketStyle;
        state.studSize = valid.forced.studSize;
      }

      this.#syncGroup('boltPattern', valid.boltPatterns, state.boltPattern);
      this.#syncGroup('studSize', studs, state.studSize, valid.forced ? [valid.forced.studSize] : null);
      this.#syncGroup('bracketStyle', valid.bracketStyles, state.bracketStyle, valid.forced ? [valid.forced.bracketStyle] : null);
      this.#syncGroup('fluidType', valid.fluidTypes, state.fluidType);

      if (this.refs.bracketForcedNote) {
        const note = this.refs.bracketForcedNote;
        if (valid.forced) {
          note.hidden = false;
          note.textContent =
            SPEED_AXLE_RULES.capacities[state.capacity].label +
            ' axles run Heavy Duty brackets with 5/8" studs. Locked in for you.';
        } else {
          note.hidden = true;
        }
      }
    }

    /* Hub type logic:
       - 10K/12K cannot be beam only (dual-wheel class parts are not cross
         compatible) and require a single/dual wheel choice.
       - Beam only has no hubs, so the bolt pattern + stud step is skipped. */
    const isBig = state.capacity === '10K' || state.capacity === '12K';

    const beamRadio = this.querySelector('input[name="hubType"][value="beam"]');
    if (beamRadio) {
      beamRadio.disabled = isBig;
      const beamOpt = beamRadio.closest('.sa-opt');
      if (beamOpt) beamOpt.hidden = isBig;
      if (isBig && state.hubType === 'beam') {
        state.hubType = null;
        beamRadio.checked = false;
      }
    }

    if (this.refs.wheelConfigBlock) this.refs.wheelConfigBlock.hidden = !isBig;
    if (!isBig && state.wheelConfig) state.wheelConfig = null;

    const beam = state.hubType === 'beam';
    const patternStep = this.querySelector('[data-step="pattern"]');
    if (patternStep) patternStep.hidden = beam;
    /* beam only has no hubs -> no bearing fluid choice; adds a hardware y/n */
    if (this.refs.fluidBlock) this.refs.fluidBlock.hidden = beam;
    if (this.refs.beamHardwareBlock) this.refs.beamHardwareBlock.hidden = !beam;
    if (beam) {
      state.boltPattern = null;
      state.studSize = null;
      state.fluidType = null;
    } else if (state.beamHardware) {
      state.beamHardware = null;
    }

    /* Brake type appears only on a braked axle, and the drum type only once
       Drum Brakes is chosen. Leaving brakes clears both, so a choice made
       earlier cannot ride along unseen into the summary or the order. The
       radios are re-synced from state, which also unchecks a cleared one. */
    const brakes = state.hubType === 'brakes';
    if (!brakes) {
      state.brakeType = null;
      state.drumType = null;
    } else if (state.brakeType !== 'drum') {
      state.drumType = null;
    }
    if (this.refs.brakeTypeBlock) this.refs.brakeTypeBlock.hidden = !brakes;
    if (this.refs.drumTypeBlock) this.refs.drumTypeBlock.hidden = !(brakes && state.brakeType === 'drum');
    this.#syncGroup('brakeType', SPEED_AXLE_RULES.brakeTypes.map((b) => b.value), state.brakeType);
    this.#syncGroup('drumType', SPEED_AXLE_RULES.drumTypes.map((d) => d.value), state.drumType);

    /* Galvanized is hot-dipped off-site and adds lead time. The warning shows
       in the finish step when chosen and again in the summary rail, which is
       what the buyer reads at submission. */
    const galvanized = state.finish === 'Galvanized';
    if (this.refs.galvanizedNote) this.refs.galvanizedNote.hidden = !galvanized;
    if (this.refs.summaryGalvanizedNote) this.refs.summaryGalvanizedNote.hidden = !galvanized;

    /* arm angle: valid positions per the matrix */
    for (const radio of this.querySelectorAll('input[name="armAngleCombo"]')) {
      const [angle, position] = radio.value.split('|');
      const positions = ARM_ANGLE_MATRIX[angle] || [];
      const ok = angle === '0' ? true : positions.includes(position);
      radio.disabled = !ok;
    }

    this.#validateMeasurements();
    this.#gateSteps();
    this.#renderSummary();
    this.#renderRideHeight();
    this.#updateSubmit();
  }

  /* Ride height readout under the dial: the spindle offsets for the chosen
     capacity and angle, and, behind a click, the chart for every angle at
     that capacity. Hidden until both are chosen; a capacity the chart does
     not cover shows a one line note instead. */
  #renderRideHeight() {
    const block = this.refs.rideHeight;
    if (!block) return;
    const state = this.state;
    const angleKey = state.armAngle == null ? null : state.armAngle + '|' + (state.armPosition || '');
    const values = state.capacity && angleKey ? rideHeightFor(state.capacity, angleKey) : null;
    const uncovered = Boolean(state.capacity && angleKey && !RIDE_HEIGHT.column[state.capacity]);

    if (this.refs.rideHeightNone) this.refs.rideHeightNone.hidden = !uncovered;
    block.hidden = !values;
    if (!values) return;

    const inches = (n) => (n < 0 ? '-' : '') + Math.abs(n).toFixed(2) + ' in';
    const capLabel = SPEED_AXLE_RULES.capacities[state.capacity].label;

    if (this.refs.rideHeightTitle) {
      this.refs.rideHeightTitle.textContent = 'Spindle position, ' + capLabel + ' at ' + this.#angleText();
    }

    const list = this.refs.rideHeightValues;
    if (list) {
      list.textContent = '';
      RIDE_HEIGHT.loads.forEach((load, i) => {
        const item = document.createElement('div');
        item.className = 'sa-ride__value';
        const dt = document.createElement('dt');
        dt.textContent = load;
        const dd = document.createElement('dd');
        dd.textContent = inches(values[i]);
        item.append(dt, dd);
        list.append(item);
      });
    }

    if (this.refs.rideHeightChartSummary) {
      this.refs.rideHeightChartSummary.textContent = 'Ride height chart: every angle at ' + capLabel;
    }

    const rows = this.refs.rideHeightRows;
    if (rows) {
      rows.textContent = '';
      for (const key of RIDE_HEIGHT.angles) {
        const row = rideHeightFor(state.capacity, key);
        if (!row) continue;
        const tr = document.createElement('tr');
        tr.classList.toggle('is-current', key === angleKey);
        const th = document.createElement('th');
        th.scope = 'row';
        th.textContent = rideHeightAngleLabel(key);
        tr.append(th);
        for (const n of row) {
          const td = document.createElement('td');
          td.textContent = inches(n);
          tr.append(td);
        }
        rows.append(tr);
      }
    }
  }

  /* Each step stays greyed out until the one above it is complete.
     gatedSteps[] holds steps 02 through 08 in DOM order. */
  #gateSteps() {
    const state = this.state;
    const beam = state.hubType === 'beam';
    const needsWheel = state.capacity === '10K' || state.capacity === '12K';
    const hubsDone =
      state.hubType !== null &&
      (!needsWheel || Boolean(state.wheelConfig)) &&
      (!beam || Boolean(state.beamHardware)) &&
      (state.hubType !== 'brakes' || Boolean(brakeTypeLabel(state))); /* brake type, and drum type under it */
    const prerequisites = [
      () => Boolean(state.capacity),                                /* 02 hubs needs capacity */
      () => hubsDone,                                               /* 03 pattern (hidden when beam) needs hubs */
      () => (beam ? hubsDone : Boolean(state.boltPattern && state.studSize)), /* 04 measurements */
      () => this.measurementsValid === true,                        /* 05 angle needs valid measurements */
      () => state.armAngle !== null,                                /* 06 brackets needs angle */
      () =>
        Boolean(
          state.bracketStyle &&
            state.bracketOrientation &&
            (!OFFER_SIDE_MOUNT || state.sideMountInstalled) &&
            state.tubeProfile
        ),                                                          /* 07 finish needs brackets step */
      () => Boolean(state.finish && (beam || state.fluidType)),     /* 08 review needs finish step (fluid n/a for beam) */
    ];

    let unlocked = true;
    (this.refs.gatedSteps || []).forEach((fieldset, index) => {
      unlocked = unlocked && prerequisites[index]();
      fieldset.disabled = !unlocked;
      fieldset.classList.toggle('is-gated', !unlocked);
    });
  }

  #validStuds(valid) {
    const cap = SPEED_AXLE_RULES.capacities[this.state.capacity];
    if (cap && cap.studsByPattern) {
      if (!this.state.boltPattern) {
        /* nothing picked yet: any stud that some valid pattern accepts */
        const set = new Set();
        for (const pattern of valid.boltPatterns) {
          for (const stud of cap.studsByPattern[pattern] || cap.studSizes) set.add(stud);
        }
        return [...set];
      }
      return cap.studsByPattern[this.state.boltPattern] || cap.studSizes;
    }
    return valid.studSizes;
  }

  #syncGroup(name, validValues, selectedValue, lockedValues) {
    for (const input of this.querySelectorAll('input[name="' + name + '"]')) {
      const isValid = validValues.includes(input.value);
      input.disabled = !isValid || (lockedValues ? !lockedValues.includes(input.value) : false);
      input.checked = input.value === selectedValue;
      input.closest('.sa-opt').classList.toggle('is-locked', Boolean(lockedValues) && isValid);
    }
  }

  /* ------------------------------------------------------------
     Measurements
     ------------------------------------------------------------ */
  #validateMeasurements() {
    const rules = SPEED_AXLE_RULES.logic.measurements;
    const hubFace = parseFloat(this.state.hubFace);
    const bracket = parseFloat(this.state.outsideBracket);
    const errorEl = this.refs.measureError;
    const okEl = this.refs.measureOk;

    if (this.refs.hubFaceLabel) {
      this.refs.hubFaceLabel.textContent = isNaN(hubFace) ? '__' : hubFace + ' in';
    }
    if (this.refs.bracketLabel) {
      this.refs.bracketLabel.textContent = isNaN(bracket) ? '__' : bracket + ' in';
    }

    let error = null;
    let overhang = null;

    if (!isNaN(hubFace) && hubFace > rules.maxHubFace) {
      error = 'Hub face cannot exceed ' + rules.maxHubFace + ' inches. Call us for wider builds.';
    }
    if (!error && !isNaN(hubFace) && !isNaN(bracket)) {
      if (rules.hubFaceGreaterThanBracket && hubFace <= bracket) {
        error = 'Hub face must be greater than the outside bracket measurement. Check the diagram: the hub face span is the wider one.';
      } else {
        overhang = (hubFace - bracket) / 2;
        const windowRule = rules.overhangWindows[this.state.capacity];
        if (windowRule && (overhang < windowRule.min || overhang > windowRule.max)) {
          error =
            'Overhang is ' + overhang.toFixed(2) + ' in per side. For ' +
            (SPEED_AXLE_RULES.capacities[this.state.capacity]
              ? SPEED_AXLE_RULES.capacities[this.state.capacity].label
              : 'this capacity') +
            ' it must be between ' + windowRule.min.toFixed(2) + ' and ' + windowRule.max.toFixed(2) + ' in.';
        }
      }
    }

    if (this.refs.overhangLabel) {
      this.refs.overhangLabel.textContent =
        overhang !== null && !error ? 'OVERHANG: ' + overhang.toFixed(2) + ' IN PER SIDE' : '';
    }

    if (errorEl) {
      errorEl.hidden = !error;
      errorEl.textContent = error || '';
    }
    if (okEl) {
      const ok = !error && overhang !== null;
      okEl.hidden = !ok;
      okEl.textContent = ok ? 'Overhang checks out: ' + overhang.toFixed(2) + ' in per side.' : '';
    }

    this.measurementsValid = !error && overhang !== null;
  }

  /* ------------------------------------------------------------
     Summary and submit gating
     ------------------------------------------------------------ */
  #summaryValue(key) {
    const state = this.state;
    switch (key) {
      case 'capacity':
        return state.capacity ? SPEED_AXLE_RULES.capacities[state.capacity].label : null;
      case 'hubs': {
        if (state.hubType === null) return null;
        if (state.hubType === 'beam') return 'Beam only, no hubs';
        if (state.hubType === 'idler') return 'Idler, no brakes';
        /* Braked: the brake type completes the row, so until it is chosen the
           row reads as not set rather than claiming a brake. */
        const brake = brakeTypeLabel(state);
        return brake ? 'Brakes, ' + brake : null;
      }
      case 'beamHardware':
        if (state.hubType !== 'beam') return undefined;
        if (!state.beamHardware) return null;
        return state.beamHardware === 'yes' ? 'Yes' : 'No';
      case 'wheelConfig': {
        const needsWheel = state.capacity === '10K' || state.capacity === '12K';
        if (!needsWheel) return undefined;
        if (!state.wheelConfig) return null;
        return state.wheelConfig === 'single' ? 'Single Wheel' : 'Dual Wheel';
      }
      case 'boltPattern':
        if (state.hubType === 'beam') return undefined;
        return state.boltPattern ? formatBoltPattern(state.boltPattern) : null;
      case 'studSize':
        if (state.hubType === 'beam') return undefined;
        return state.studSize;
      case 'hubFace':
        return state.hubFace ? state.hubFace + ' in' : null;
      case 'outsideBracket':
        return state.outsideBracket ? state.outsideBracket + ' in' : null;
      case 'armAngle':
        return state.armAngle !== null ? this.#angleText() : null;
      case 'tubeProfile':
        return state.tubeProfile;
      case 'bracketStyle':
        return state.bracketStyle ? BRACKET_STYLE_LABELS[state.bracketStyle] : null;
      case 'bracketOrientation':
        return state.bracketOrientation ? ORIENTATION_LABELS[state.bracketOrientation] : null;
      case 'sideMountInstalled':
        if (!OFFER_SIDE_MOUNT) return undefined;
        return state.sideMountInstalled ? (state.sideMountInstalled === 'yes' ? 'Yes' : 'No') : null;
      case 'finish':
        return state.finish;
      case 'fluidType':
        if (state.hubType === 'beam') return undefined;
        return state.fluidType;
      case 'quantity':
        return String(state.quantity);
      default:
        return null;
    }
  }

  #renderSummary() {
    const summary = this.refs.summary;
    if (!summary) return;
    summary.textContent = '';
    for (const [key, label] of SUMMARY_ROWS) {
      const value = this.#summaryValue(key);
      if (value === undefined) continue; /* not applicable for this config */
      const dt = document.createElement('dt');
      dt.textContent = label;
      const dd = document.createElement('dd');
      dd.textContent = value || 'Not set';
      dd.classList.toggle('is-empty', !value);
      summary.append(dt, dd);
    }
  }

  #missingFields() {
    const missing = [];
    const state = this.state;
    if (!state.capacity) missing.push('capacity');
    if (state.hubType === null) missing.push('hubs');
    if (state.hubType === 'brakes' && !state.brakeType) missing.push('brake type');
    if (state.hubType === 'brakes' && state.brakeType === 'drum' && !state.drumType) {
      missing.push('drum brake type');
    }
    if ((state.capacity === '10K' || state.capacity === '12K') && !state.wheelConfig) {
      missing.push('wheel configuration');
    }
    if (state.hubType === 'beam' && !state.beamHardware) {
      missing.push('hardware option');
    }
    if (state.hubType !== 'beam') {
      if (!state.boltPattern) missing.push('bolt pattern');
      if (!state.studSize) missing.push('stud size');
    }
    if (!this.measurementsValid) missing.push('valid measurements');
    if (state.armAngle === null) missing.push('arm angle');
    if (!state.tubeProfile) missing.push('tube profile');
    if (!state.bracketStyle) missing.push('bracket style');
    if (!state.bracketOrientation) missing.push('bracket orientation');
    if (OFFER_SIDE_MOUNT && !state.sideMountInstalled) missing.push('side mount brackets');
    if (!state.finish) missing.push('finish');
    if (state.hubType !== 'beam' && !state.fluidType) missing.push('fluid type');
    if (!state.customerFirstName.trim()) missing.push('your first name');
    if (!state.customerLastName.trim()) missing.push('your last name');
    if (!this.#emailValid(state.customerEmail)) missing.push('a valid email');
    if (
      !state.customerAddress1.trim() ||
      !state.customerCity.trim() ||
      !state.customerState.trim() ||
      !state.customerZip.trim()
    ) {
      missing.push('shipping address');
    }
    if (!state.termsAccepted) missing.push('terms acknowledgment');
    return missing;
  }

  #emailValid(email) {
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((email || '').trim());
  }

  #updateSubmit() {
    const missing = this.#missingFields();
    const hasEndpoint = Boolean(this.dataset.quoteEndpoint);
    const ready = missing.length === 0 && hasEndpoint;

    if (this.refs.submitButton) {
      this.refs.submitButton.disabled = !ready;
    }
    if (this.refs.submitHint) {
      if (ready) {
        this.refs.submitHint.textContent = 'Spec complete. Submitting sends it to Hydrastar for a quote, no payment is taken.';
      } else if (!hasEndpoint) {
        this.refs.submitHint.textContent = 'The builder is not connected yet. Call or email your spec instead.';
      } else if (missing.length > 0 && missing.length <= 3) {
        this.refs.submitHint.textContent = 'Still needed: ' + missing.join(', ') + '.';
      } else {
        this.refs.submitHint.textContent = '';
      }
    }
  }

  /* ------------------------------------------------------------
     Submission: POST the spec to the quote endpoint, which creates
     a Shopify draft order (specs become line item properties).
     No cart, no checkout, no payment.
     ------------------------------------------------------------ */
  #buildProperties() {
    const state = this.state;
    const properties = {};

    /* Hydrastar's internal part number, first so it heads the spec sheet and
       the line item properties in admin. Empty until every component of it is
       chosen, which by the review step it always is. */
    const partNumber = buildPartNumber(state);
    if (partNumber) properties['Part Number'] = partNumber;

    /* Right under the part number so the office sees time-critical requests
       before anything else on the spec sheet. */
    properties['Urgency'] =
      state.urgency === 'immediate'
        ? 'IMMEDIATE NEED — customer is time-critical'
        : 'Standard order';

    /* The next eight rows are the part number's components, in the order they
       appear in it, so the office can read straight down the sheet and check
       the generated number against the spec: the Hubs row is the last segment
       (I for idler) and, on a braked axle, the Brake Type row right after it.
       Everything else is the rest of the build, which the number does not
       encode. */
    properties['Capacity'] = SPEED_AXLE_RULES.capacities[state.capacity].label + ' (' + state.capacity + ')';
    properties['Tube Profile'] = state.tubeProfile;
    properties['Hub Face'] = state.hubFace + ' in';
    properties['Outside Bracket'] = state.outsideBracket + ' in';
    properties['Bracket Style'] = BRACKET_STYLE_LABELS[state.bracketStyle];
    properties['Bracket Orientation'] = ORIENTATION_LABELS[state.bracketOrientation];
    properties['Finish'] = state.finish;
    properties['Arm Angle'] =
      state.armAngle === '0'
        ? '0 degrees'
        : state.armAngle + ' degrees ' + (state.armPosition === 'upturn' ? 'Upturn (-)' : 'Downturn (+)');

    if (state.hubType === 'beam') {
      properties['Hubs'] = 'Beam only, no hubs';
      properties['Hardware'] = state.beamHardware === 'yes' ? 'Yes' : 'No';
    } else {
      properties['Hubs'] = state.hubType === 'idler' ? 'Idler, no brakes' : 'Brake';
      /* Its own row, matching the Brake Type line on the paper form. */
      if (state.hubType === 'brakes') properties['Brake Type'] = brakeTypeLabel(state);
    }
    if (state.wheelConfig) {
      properties['Wheel Configuration'] = state.wheelConfig === 'single' ? 'Single Wheel' : 'Dual Wheel';
    }
    if (state.hubType !== 'beam') {
      properties['Bolt Pattern'] = formatBoltPattern(state.boltPattern);
      properties['Stud Size'] = state.studSize;
    }
    if (OFFER_SIDE_MOUNT) {
      properties['Side Mount Brackets Installed'] = state.sideMountInstalled === 'yes' ? 'Yes' : 'No';
    }
    if (state.hubType !== 'beam') properties['Fluid Type'] = state.fluidType;
    properties['Overhang (calculated)'] =
      (((parseFloat(state.hubFace) - parseFloat(state.outsideBracket)) / 2).toFixed(2)) + ' in per side';
    if (state.po) properties['PO Number'] = state.po;
    properties['Terms Accepted'] =
      'Yes: non-refundable, paid in full before work starts (single invoice)';
    return properties;
  }

  async submit() {
    const endpoint = this.dataset.quoteEndpoint;
    if (this.#missingFields().length > 0 || !endpoint) return;

    const button = this.refs.submitButton;
    const errorEl = this.refs.submitError;
    button.disabled = true;
    button.classList.add('is-loading');
    if (errorEl) errorEl.hidden = true;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          customer: {
            firstName: this.state.customerFirstName.trim(),
            lastName: this.state.customerLastName.trim(),
            company: this.state.customerCompany.trim(),
            email: this.state.customerEmail.trim(),
            phone: this.state.customerPhone.trim(),
            address: {
              address1: this.state.customerAddress1.trim(),
              address2: this.state.customerAddress2.trim(),
              city: this.state.customerCity.trim(),
              province: this.state.customerState.trim(),
              zip: this.state.customerZip.trim(),
              country: 'US',
            },
          },
          quantity: this.state.quantity,
          po: this.state.po,
          spec: this.#buildProperties(),
        }),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data || !data.ok) {
        throw new Error((data && data.error) || 'Could not send your quote request.');
      }

      this.#showSuccess(data.name);
    } catch (error) {
      button.disabled = false;
      button.classList.remove('is-loading');
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.textContent =
          (error && error.message ? error.message : 'Something went wrong.') +
          ' Try again, or call us and we will take your spec by phone.';
      }
    }
  }

  #showSuccess(reference) {
    if (this.refs.layout) this.refs.layout.hidden = true;

    const panel = this.refs.successPanel;
    if (!panel) return;

    /* The draft order number is the strongest proof the submit landed, so
       show it when Shopify gave us one and stay quiet when it did not. */
    if (reference && this.refs.successRef && this.refs.successRefValue) {
      this.refs.successRefValue.textContent = reference;
      this.refs.successRef.hidden = false;
    }

    panel.hidden = false;

    /* Align to the top rather than centring: the card is tall enough that
       centring can push the heading above the fold on a laptop, which is the
       whole reason the old confirmation was easy to miss. */
    panel.scrollIntoView({ behavior: 'smooth', block: 'start' });

    /* Move focus so screen reader and keyboard users land on the
       confirmation instead of the now hidden builder. */
    panel.focus({ preventScroll: true });
  }
}

if (!customElements.get('speed-axle-configurator-component')) {
  customElements.define('speed-axle-configurator-component', SpeedAxleConfiguratorComponent);
}
